import React, { useEffect, useState, useMemo } from "react";
import { Form, Col, Row, OverlayTrigger, Tooltip } from "react-bootstrap";
import { mapUserRoles } from "../../../functions/useRoleMapping";
import { useSelector, useDispatch } from "react-redux";
import useHandleBlur from "../../../functions/useHandleBlur";
import { useFormState } from "../../../functions/useFormState"; // Reusable form hook
import { selectApiStatus } from "../../../redux/slices/apiStatusSlice";
import {
  selectUserDetails,
  selectUserRoles,
} from "../../../redux/slices/userSlice";
import { selectApiUrls } from "../../../redux/slices/apiUrlSlice";
import {
  selectStationEnclosure,
  selectGeneralStation,
  selectFieldDescriptions,
} from "../../../redux/selectors";
import useFieldClass from "../../../functions/useFieldClass";
import NoRecordFoundAlert from "../../common/NoRecordFoundAlert";
import {
  getNearestCsmValidLength,
  getNearestCsmValidWidth,
  isCsmStandardLength,
  isCsmStandardSize,
  isCsmStandardWidth,
  isCsmWindRatedSize,
} from "./stationEnclosureSizing";

const StationEnclosure = (zone) => {
  const dispatch = useDispatch();
  const userDetails = useSelector(selectUserDetails); // User details
  const userRoles = useSelector(selectUserRoles); // User details
  const currentUser = userDetails?.name;
  const sectionName = "stationEnclosure"; // Name of the component

  const apiUrls = useSelector(selectApiUrls); // Fetch API URLs from Redux
  const apiUrl = apiUrls[sectionName];
  const tableKey = sectionName;

  const { options } = useSelector((state) => state.dropdown); // Fetch dropdown values from redux

  const storedJobDetails = useSelector(selectStationEnclosure);

  const activetab = useSelector(
    (state) => state.activeMechanicalTab.selectedZone
  );

  const generalSpecsDetails = useSelector(selectGeneralStation);

  const stationColor =
    generalSpecsDetails.find((detail) => detail.Zone === activetab)?.Color ||
    "";

  const apiStatus = useSelector(selectApiStatus); // Fetch API status from Redux
  const loading = apiStatus[sectionName] !== "success"; // Determine loading state based on API status

  // const loading = storedJobDetails.length === 0;
  const error = null;

  const jobDetails = useMemo(() => {
    const detail = storedJobDetails.find((detail) => detail.Zone === activetab);
    return detail || storedJobDetails[0] || {};
  }, [storedJobDetails, activetab]);

  const { formData, handleChange, setFormData } = useFormState({});

  useEffect(() => {
    if (jobDetails) {
      setFormData({
        ID: jobDetails.ID || "",
        acousticInsulation: jobDetails.AcousticInsulation || "",
        baseLength: jobDetails.BaseLength || "",
        baseWidth: jobDetails.BaseWidth || "",
        stationEnclosureComments: jobDetails.StationEnclosureComments || "",
        coolingFans: jobDetails.CoolingFans || "",
        enclosurePaintColor: jobDetails.EnclosurePaintColor || "",
        enclosureType: jobDetails.EnclosureType || "",
        exteriorLighting: jobDetails.ExteriorLighting || "",
        externalPowerConnection: jobDetails.ExternalPowerConnection || "",
        hasEnclosure: jobDetails.HasEnclosure || "",
        interiorLighting: jobDetails.InteriorLighting || "",
        snowWindRating: jobDetails.SnowWindRating || "",
        specialHeight: jobDetails.SpecialHeight || "",
        thermalInsulation: jobDetails.ThermalInsulation || "",
        peStampRequired: jobDetails.PEStampRequired || "", // Add new field
      });
    }
  }, [dispatch, jobDetails, setFormData]);

  const jobCheckoutStatus = useSelector((state) => state.checkout.job);
  const isJobCheckedOut = jobCheckoutStatus?.IsCheckedOut === true;
  const isCurrentUserCheckedOut =
    jobCheckoutStatus?.CheckedOutBy === currentUser;
  const readOnly = useSelector((state) => state.readOnly.isReadOnly); // Fetch read-only state from Redux
  const isDisabled = (isJobCheckedOut && !isCurrentUserCheckedOut) || readOnly; // Update isDisabled condition

  // State to store role variables
  const [roles, setRoles] = useState({
    Admin: false,
    Apps: false,
    Engineer: false,
    TestPit: false,
    PSN: false,
    Sales: false,
    ReadOnly: false,
  });

  const { handleFocus, handleBlur, showNotification, saveStatus } =
    useHandleBlur(formData);

  useEffect(() => {
    setRoles(mapUserRoles(userRoles));
  }, [userRoles]);

  const fieldClasses = useFieldClass(formData, zone); // Use useFieldClass hook

  const fieldDescriptions = useSelector(selectFieldDescriptions);

  const getFieldTitle = (fieldName) => {
    return (
      fieldDescriptions.find((desc) => desc.ColumnName === fieldName)
        ?.Description || ""
    );
  };

  const renderTooltip = (fieldName) => (
    <Tooltip id={`tooltip-${fieldName}`}>
      {getFieldTitle(fieldName)
        .split("\n")
        .map((line, index) => (
          <span key={index}>
            {line}
            <br />
          </span>
        ))}
    </Tooltip>
  );

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center">
        <div className="spinner-border" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    ); // Show loading spinner
  }

  if (error) {
    return <div className="error">{error}</div>; // Show error message if fetch fails
  }

  if (!loading && !jobDetails?.ID) {
    return <NoRecordFoundAlert sectionName={sectionName} zone={zone} />;
  }

  /****************Add dropdown options here*************************/
  const enclosureTypeOptions =
    options?.filter((option) => option.DropdownType === "Enclosure Type").sort((a, b) => a.SortOrder - b.SortOrder) || [];
  const enclosurePaintColorOptions =
    options?.filter((option) => option.DropdownType === "Color").sort((a, b) => a.SortOrder - b.SortOrder) || [];
  const genericOptions =
    options?.filter((option) => option.DropdownType === "Generic Yes/ No").sort((a, b) => a.SortOrder - b.SortOrder) || [];
  const exteriorLightingOptions =
    options?.filter((option) => option.DropdownType === "Exterior Lighting").sort((a, b) => a.SortOrder - b.SortOrder) || [];
  const snowWindRatingOptions =
    options?.filter((option) => option.DropdownType === "Generic Yes/ No").sort((a, b) => a.SortOrder - b.SortOrder) || [];
  const coolingFansOptions =
    options?.filter((option) => option.DropdownType === "Cooling Fans").sort((a, b) => a.SortOrder - b.SortOrder) || [];
  const specialHeightOptions =
    options?.filter((option) => option.DropdownType === "Special Height").sort((a, b) => a.SortOrder - b.SortOrder) || [];
  const externalPowerConnectionOptions =
    options?.filter((option) => option.DropdownType === "External Power Connection").sort((a, b) => a.SortOrder - b.SortOrder) || [];
  /***************************************************************** */

  // Check if Snow Wind Rating is set to "Yes"
  const showPEStampField = formData.snowWindRating === "Yes";

  const isValueInDropdown = (value, dropdownOptions) => {
    const exists = dropdownOptions.some(
      (option) => option.DisplayText === value || option.displayName === value
    );
    return exists;
  };

  const isCsmEnclosure = formData.enclosureType === "CSM";
  const hasSelectedLength =
    String(formData.baseLength ?? "").trim() !== "";
  const hasSelectedWidth = String(formData.baseWidth ?? "").trim() !== "";
  const hasSelectedCsmSize =
    isCsmEnclosure && hasSelectedLength && hasSelectedWidth;
  const hasStandardCsmLength = isCsmStandardLength(formData.baseLength);
  const hasStandardCsmWidth = isCsmStandardWidth(formData.baseWidth);
  const hasStandardCsmDimensions =
    hasStandardCsmLength && hasStandardCsmWidth;
  const isStandardCsmSize =
    hasSelectedCsmSize &&
    isCsmStandardSize(formData.baseLength, formData.baseWidth);
  const showNonStandardSizeWarning =
    hasSelectedCsmSize && hasStandardCsmDimensions && !isStandardCsmSize;
  const showWindRatingSizeWarning =
    isStandardCsmSize &&
    formData.snowWindRating === "Yes" &&
    !isCsmWindRatedSize(formData.baseLength, formData.baseWidth);
  const nearestValidLength = getNearestCsmValidLength(
    formData.baseLength,
    formData.baseWidth
  );
  const nearestValidWidth = getNearestCsmValidWidth(
    formData.baseLength,
    formData.baseWidth
  );

  return (
    <div>
      {error ? (
        <p style={{ color: "red" }}>Error: {error}</p>
      ) : !formData ? (
        <p>Loading...</p>
      ) : (
        <div>
          <div>
            {showNotification && (
              <div
                className={`save-notification ${
                  saveStatus.includes("Failed") ? "error" : ""
                }`}
              >
                {saveStatus}
              </div>
            )}
          </div>
          <div className="mechanical-responsive-grid">
            <Row className="mb-3">
              <Col>
                <Form.Group as={Row} controlId="enclosureType">
                  <Form.Label column md={3}>
                    <OverlayTrigger
                      placement="top"
                      overlay={renderTooltip("EnclosureType")}
                      delay={{ show: 300, hide: 200 }}
                    >
                      <span>Enclosure Type</span>
                    </OverlayTrigger>
                  </Form.Label>
                  <Col>
                    <Form.Select
                      name="enclosureType"
                      value={formData.enclosureType || ""}
                      onFocus={handleFocus}
                      onBlur={(e) =>
                        handleBlur(e, "Enclosure Type", enclosureTypeOptions, {
                          ID: formData.ID,
                          url: apiUrl,
                          field: "EnclosureType",
                          tableKey,
                        })
                      }
                      disabled={isDisabled || (!roles.Admin && !roles.Apps)}
                      className={fieldClasses.enclosureType}
                      onChange={handleChange}
                    >
                      <option value="">Select Enclosure Type</option>
                      {enclosureTypeOptions.map((option) => (
                        <option key={option.ID} value={option.DisplayText}>
                          {option.DisplayText}
                        </option>
                      ))}
                      {!isValueInDropdown(formData.enclosureType, enclosureTypeOptions) &&
                        formData.enclosureType && (
                          <option value={formData.enclosureType} style={{ color: "red" }}>
                            *{formData.enclosureType}
                          </option>
                        )}
                    </Form.Select>
                  </Col>
                </Form.Group>
              </Col>

              <Col>
                <Form.Group as={Row} controlId="enclosurePaintColor">
                  <Form.Label column md={3}>
                    <OverlayTrigger
                      placement="top"
                      overlay={renderTooltip("EnclosurePaintColor")}
                      delay={{ show: 300, hide: 200 }}
                    >
                      <span>Enclosure Paint Color</span>
                    </OverlayTrigger>
                  </Form.Label>
                  <Col>
                    <Form.Select
                      name="enclosurePaintColor"
                      value={formData.enclosurePaintColor || ""}
                      onFocus={handleFocus}
                      onBlur={(e) =>
                        handleBlur(
                          e,
                          "Enclosure Paint Color",
                          enclosurePaintColorOptions,
                          {
                            ID: formData.ID,
                            url: apiUrl,
                            field: "EnclosurePaintColor",
                            tableKey,
                          }
                        )
                      }
                      disabled={isDisabled || (!roles.Admin && !roles.Apps)}
                      className={fieldClasses.enclosurePaintColor}
                      onChange={handleChange}
                    >
                      <option value="">Select Enclosure Paint Color</option>
                      {enclosurePaintColorOptions.map((option) => (
                        <option key={option.ID} value={option.DisplayText}>
                          {option.DisplayText === "Same as Station"
                            ? `Same as Station (${stationColor})`
                            : option.DisplayText}
                        </option>
                      ))}
                      {!isValueInDropdown(formData.enclosurePaintColor, enclosurePaintColorOptions) &&
                        formData.enclosurePaintColor && (
                          <option value={formData.enclosurePaintColor} style={{ color: "red" }}>
                            *{formData.enclosurePaintColor}
                          </option>
                        )}
                    </Form.Select>
                  </Col>
                </Form.Group>
              </Col>
            </Row>
            <Row className="mb-3">
              <Col>
                <Form.Group as={Row} controlId="acousticInsulation">
                  <Form.Label column md={3}>
                    <OverlayTrigger
                      placement="top"
                      overlay={renderTooltip("AcousticInsulation")}
                      delay={{ show: 300, hide: 200 }}
                    >
                      <span>Acoustic Insulation</span>
                    </OverlayTrigger>
                  </Form.Label>
                  <Col>
                    <Form.Select
                      name="acousticInsulation"
                      value={formData.acousticInsulation || ""}
                      onFocus={handleFocus}
                      onBlur={(e) =>
                        handleBlur(e, "Acoustic Insulation", genericOptions, {
                          ID: formData.ID,
                          url: apiUrl,
                          field: "AcousticInsulation",
                          tableKey,
                        })
                      }
                      disabled={isDisabled || (!roles.Admin && !roles.Apps)}
                      className={fieldClasses.acousticInsulation}
                      onChange={handleChange}
                    >
                      <option value="">Select Acoustic Insulation</option>
                      {genericOptions.map((option) => (
                        <option key={option.ID} value={option.DisplayText}>
                          {option.DisplayText}
                        </option>
                      ))}
                      {!isValueInDropdown(formData.acousticInsulation, genericOptions) &&
                        formData.acousticInsulation && (
                          <option value={formData.acousticInsulation} style={{ color: "red" }}>
                            *{formData.acousticInsulation}
                          </option>
                        )}
                    </Form.Select>
                  </Col>
                </Form.Group>
              </Col>

              <Col>
                <Form.Group as={Row} controlId="thermalInsulation">
                  <Form.Label column md={3}>
                    <OverlayTrigger
                      placement="top"
                      overlay={renderTooltip("ThermalInsulation")}
                      delay={{ show: 300, hide: 200 }}
                    >
                      <span>Thermal Insulation</span>
                    </OverlayTrigger>
                  </Form.Label>
                  <Col>
                    <Form.Select
                      name="thermalInsulation"
                      value={formData.thermalInsulation || ""}
                      onFocus={handleFocus}
                      onBlur={(e) =>
                        handleBlur(e, "Thermal Insulation", genericOptions, {
                          ID: formData.ID,
                          url: apiUrl,
                          field: "ThermalInsulation",
                          tableKey,
                        })
                      }
                      disabled={isDisabled || (!roles.Admin && !roles.Apps)}
                      className={fieldClasses.thermalInsulation}
                      onChange={handleChange}
                    >
                      <option value="">Select Thermal Insulation</option>
                      {genericOptions.map((option) => (
                        <option key={option.ID} value={option.DisplayText}>
                          {option.DisplayText}
                        </option>
                      ))}
                      {!isValueInDropdown(formData.thermalInsulation, genericOptions) &&
                        formData.thermalInsulation && (
                          <option value={formData.thermalInsulation} style={{ color: "red" }}>
                            *{formData.thermalInsulation}
                          </option>
                        )}
                    </Form.Select>
                  </Col>
                </Form.Group>
              </Col>
            </Row>
            <Row className="mb-3">
              <Col>
                <Form.Group as={Row} controlId="exteriorLighting">
                  <Form.Label column md={3}>
                    <OverlayTrigger
                      placement="top"
                      overlay={renderTooltip("ExteriorLighting")}
                      delay={{ show: 300, hide: 200 }}
                    >
                      <span>Exterior Lighting</span>
                    </OverlayTrigger>
                  </Form.Label>
                  <Col>
                    <Form.Select
                      name="exteriorLighting"
                      value={formData.exteriorLighting || ""}
                      onFocus={handleFocus}
                      onBlur={(e) =>
                        handleBlur(
                          e,
                          "Exterior Lighting",
                          exteriorLightingOptions,
                          {
                            ID: formData.ID,
                            url: apiUrl,
                            field: "ExteriorLighting",
                            tableKey,
                          }
                        )
                      }
                      disabled={isDisabled || (!roles.Admin && !roles.Apps)}
                      className={fieldClasses.exteriorLighting}
                      onChange={handleChange}
                    >
                      <option value="">Select Exterior Lighting</option>
                      {exteriorLightingOptions.map((option) => (
                        <option key={option.ID} value={option.DisplayText}>
                          {option.DisplayText}
                        </option>
                      ))}
                      {!isValueInDropdown(formData.exteriorLighting, exteriorLightingOptions) &&
                        formData.exteriorLighting && (
                          <option value={formData.exteriorLighting} style={{ color: "red" }}>
                            *{formData.exteriorLighting}
                          </option>
                        )}
                    </Form.Select>
                  </Col>
                </Form.Group>
              </Col>

              <Col>
                <Form.Group as={Row} controlId="interiorLighting">
                  <Form.Label column md={3}>
                    <OverlayTrigger
                      placement="top"
                      overlay={renderTooltip("InteriorLighting")}
                      delay={{ show: 300, hide: 200 }}
                    >
                      <span>Interior Lighting</span>
                    </OverlayTrigger>
                  </Form.Label>
                  <Col>
                    <Form.Select
                      name="interiorLighting"
                      value={formData.interiorLighting || ""}
                      onFocus={handleFocus}
                      onBlur={(e) =>
                        handleBlur(e, "Interior Lighting", genericOptions, {
                          ID: formData.ID,
                          url: apiUrl,
                          field: "InteriorLighting",
                          tableKey,
                        })
                      }
                      disabled={isDisabled || (!roles.Admin && !roles.Apps)}
                      className={fieldClasses.interiorLighting}
                      onChange={handleChange}
                    >
                      <option value="">Select Interior Lighting</option>
                      {genericOptions.map((option) => (
                        <option key={option.ID} value={option.DisplayText}>
                          {option.DisplayText}
                        </option>
                      ))}
                      {!isValueInDropdown(formData.interiorLighting, genericOptions) &&
                        formData.interiorLighting && (
                          <option value={formData.interiorLighting} style={{ color: "red" }}>
                            *{formData.interiorLighting}
                          </option>
                        )}
                    </Form.Select>
                  </Col>
                </Form.Group>
              </Col>
            </Row>
            <Row className="mb-3">
              <Col>
                <Form.Group as={Row} controlId="snowWindRating">
                  <Form.Label column md={3}>
                    <OverlayTrigger
                      placement="top"
                      overlay={renderTooltip("SnowWindRating")}
                      delay={{ show: 300, hide: 200 }}
                    >
                      <span>Snow Wind Rating</span>
                    </OverlayTrigger>
                  </Form.Label>
                  <Col>
                    <Form.Select
                      name="snowWindRating"
                      value={formData.snowWindRating || ""}
                      onFocus={handleFocus}
                      onBlur={(e) =>
                        handleBlur(
                          e,
                          "Snow Wind Rating",
                          snowWindRatingOptions,
                          {
                            ID: formData.ID,
                            url: apiUrl,
                            field: "SnowWindRating",
                            tableKey,
                          }
                        )
                      }
                      disabled={isDisabled || (!roles.Admin && !roles.Apps)}
                      className={fieldClasses.snowWindRating}
                      onChange={handleChange}
                    >
                      <option value="">Select Snow Wind Rating</option>
                      {snowWindRatingOptions.map((option) => (
                        <option key={option.ID} value={option.DisplayText}>
                          {option.DisplayText}
                        </option>
                      ))}
                      {!isValueInDropdown(formData.snowWindRating, snowWindRatingOptions) &&
                        formData.snowWindRating && (
                          <option value={formData.snowWindRating} style={{ color: "red" }}>
                            *{formData.snowWindRating}
                          </option>
                        )}
                    </Form.Select>
                    {showWindRatingSizeWarning && (
                      <p style={{ color: "red" }}>
                        WARNING selected enclosure size cannot be wind rated
                      </p>
                    )}
                  </Col>
                </Form.Group>
              </Col>

              {/* Conditionally render PE Stamp Required field or empty Col */}
              {showPEStampField ? (
                <Col>
                  <Form.Group as={Row} controlId="peStampRequired">
                    <Form.Label column md={3}>
                      <OverlayTrigger
                        placement="top"
                        overlay={renderTooltip("PEStampRequired")}
                        delay={{ show: 300, hide: 200 }}
                      >
                        <span>PE Stamp Required</span>
                      </OverlayTrigger>
                    </Form.Label>
                    <Col>
                      <Form.Select
                        name="peStampRequired"
                        value={formData.peStampRequired || ""}
                        onFocus={handleFocus}
                        onBlur={(e) =>
                          handleBlur(e, "PE Stamp Required", genericOptions, {
                            ID: formData.ID,
                            url: apiUrl,
                            field: "PEStampRequired",
                            tableKey,
                          })
                        }
                        disabled={isDisabled || (!roles.Admin && !roles.Apps)}
                        className={fieldClasses.peStampRequired}
                        onChange={handleChange}
                      >
                        <option value="">Select PE Stamp Required</option>
                        {genericOptions.map((option) => (
                          <option key={option.ID} value={option.DisplayText}>
                            {option.DisplayText}
                          </option>
                        ))}
                        {!isValueInDropdown(formData.peStampRequired, genericOptions) &&
                          formData.peStampRequired && (
                            <option value={formData.peStampRequired} style={{ color: "red" }}>
                              *{formData.peStampRequired}
                            </option>
                          )}
                      </Form.Select>
                    </Col>
                  </Form.Group>
                </Col>
              ) : (
                <Col />
              )}
            </Row>
            <Row className="mb-3">
              <Col>
                <Form.Group as={Row} controlId="coolingFans">
                  <Form.Label column md={3}>
                    <OverlayTrigger
                      placement="top"
                      overlay={renderTooltip("CoolingFans")}
                      delay={{ show: 300, hide: 200 }}
                    >
                      <span>Cooling Fans</span>
                    </OverlayTrigger>
                  </Form.Label>
                  <Col>
                    <Form.Select
                      name="coolingFans"
                      value={formData.coolingFans || ""}
                      onFocus={handleFocus}
                      onBlur={(e) =>
                        handleBlur(e, "Cooling Fans", coolingFansOptions, {
                          ID: formData.ID,
                          url: apiUrl,
                          field: "CoolingFans",
                          tableKey,
                        })
                      }
                      disabled={isDisabled || (!roles.Admin && !roles.Apps)}
                      className={fieldClasses.coolingFans}
                      onChange={handleChange}
                    >
                      <option value="">Select Cooling Fans</option>
                      {coolingFansOptions.map((option) => (
                        <option key={option.ID} value={option.DisplayText}>
                          {option.DisplayText}
                        </option>
                      ))}
                      {!isValueInDropdown(formData.coolingFans, coolingFansOptions) &&
                        formData.coolingFans && (
                          <option value={formData.coolingFans} style={{ color: "red" }}>
                            *{formData.coolingFans}
                          </option>
                        )}
                    </Form.Select>
                  </Col>
                </Form.Group>
              </Col>
              <Col>
                <Form.Group as={Row} controlId="specialHeight">
                  <Form.Label column md={3}>
                    <OverlayTrigger
                      placement="top"
                      overlay={renderTooltip("SpecialHeight")}
                      delay={{ show: 300, hide: 200 }}
                    >
                      <span>Special Height</span>
                    </OverlayTrigger>
                  </Form.Label>
                  <Col>
                    <Form.Select
                      name="specialHeight"
                      value={formData.specialHeight || ""}
                      onFocus={handleFocus}
                      onBlur={(e) =>
                        handleBlur(e, "Special Height", specialHeightOptions, {
                          ID: formData.ID,
                          url: apiUrl,
                          field: "SpecialHeight",
                          tableKey,
                        })
                      }
                      disabled={isDisabled || (!roles.Admin && !roles.Apps)}
                      className={fieldClasses.specialHeight}
                      onChange={handleChange}
                    >
                      <option value="">Select Special Height</option>
                      {specialHeightOptions.map((option) => (
                        <option key={option.ID} value={option.DisplayText}>
                          {option.DisplayText}
                        </option>
                      ))}
                      {!isValueInDropdown(formData.specialHeight, specialHeightOptions) &&
                        formData.specialHeight && (
                          <option value={formData.specialHeight} style={{ color: "red" }}>
                            *{formData.specialHeight}
                          </option>
                        )}
                    </Form.Select>
                  </Col>
                </Form.Group>
              </Col>
            </Row>
            <Row className="mb-3">
              <Col>
                <Form.Group as={Row} controlId="externalPowerConnection">
                  <Form.Label column md={3}>
                    <OverlayTrigger
                      placement="top"
                      overlay={renderTooltip("ExternalPowerConnection")}
                      delay={{ show: 300, hide: 200 }}
                    >
                      <span>External Power Connection</span>
                    </OverlayTrigger>
                  </Form.Label>
                  <Col>
                    <Form.Select
                      name="externalPowerConnection"
                      value={formData.externalPowerConnection || ""}
                      onFocus={handleFocus}
                      onBlur={(e) =>
                        handleBlur(
                          e,
                          "External Power Connection",
                          externalPowerConnectionOptions,
                          {
                            ID: formData.ID,
                            url: apiUrl,
                            field: "ExternalPowerConnection",
                            tableKey,
                          }
                        )
                      }
                      disabled={isDisabled || (!roles.Admin && !roles.Apps)}
                      className={fieldClasses.externalPowerConnection}
                      onChange={handleChange}
                    >
                      <option value="">Select External Power Connection</option>
                      {externalPowerConnectionOptions.map((option) => (
                        <option key={option.ID} value={option.DisplayText}>
                          {option.DisplayText}
                        </option>
                      ))}
                      {!isValueInDropdown(formData.externalPowerConnection, externalPowerConnectionOptions) &&
                        formData.externalPowerConnection && (
                          <option value={formData.externalPowerConnection} style={{ color: "red" }}>
                            *{formData.externalPowerConnection}
                          </option>
                        )}
                    </Form.Select>
                  </Col>
                </Form.Group>
              </Col>
              <Col />
            </Row>
            <Row className="mb-3">
              <Col>
                <Form.Group as={Row} controlId="baseLength">
                  <Form.Label column md={3}>
                    <OverlayTrigger
                      placement="top"
                      overlay={renderTooltip("BaseLength")}
                      delay={{ show: 300, hide: 200 }}
                    >
                      <span>Base Length (in)</span>
                    </OverlayTrigger>
                  </Form.Label>
                  <Col>
                    <Form.Control
                      type="text"
                      name="baseLength"
                      value={formData.baseLength || ""}
                      onFocus={handleFocus}
                      onBlur={(e) =>
                        handleBlur(e, "Base Length", [], {
                          ID: formData.ID,
                          url: apiUrl,
                          field: "BaseLength",
                          tableKey,
                        })
                      }
                      disabled={isDisabled || (!roles.Admin && !roles.Apps)}
                      className={fieldClasses.baseLength}
                      onChange={handleChange}
                      placeholder="Enter Base Length"
                    />
                    {isCsmEnclosure &&
                      hasSelectedLength &&
                      !hasStandardCsmLength && (
                        <p style={{ color: "red" }}>
                          WARNING Length is outside of standards.
                          {nearestValidLength !== null &&
                            ` Nearest valid length: ${nearestValidLength} in.`}
                        </p>
                      )}
                    {showNonStandardSizeWarning && (
                      <p style={{ color: "red" }}>
                        WARNING Length is not valid with the selected width.
                        {nearestValidLength !== null &&
                          ` Nearest valid length: ${nearestValidLength} in.`}
                      </p>
                    )}
                  </Col>
                </Form.Group>
              </Col>

              <Col>
                <Form.Group as={Row} controlId="baseWidth">
                  <Form.Label column md={3}>
                    <OverlayTrigger
                      placement="top"
                      overlay={renderTooltip("BaseWidth")}
                      delay={{ show: 300, hide: 200 }}
                    >
                      <span>Base Width (in)</span>
                    </OverlayTrigger>
                  </Form.Label>
                  <Col>
                    <Form.Control
                      type="text"
                      name="baseWidth"
                      value={formData.baseWidth || ""}
                      onFocus={handleFocus}
                      onBlur={(e) =>
                        handleBlur(e, "Base Width", [], {
                          ID: formData.ID,
                          url: apiUrl,
                          field: "BaseWidth",
                          tableKey,
                        })
                      }
                      disabled={isDisabled || (!roles.Admin && !roles.Apps)}
                      className={fieldClasses.baseWidth}
                      onChange={handleChange}
                      placeholder="Enter Base Width"
                    />
                    {isCsmEnclosure &&
                      hasSelectedWidth &&
                      !hasStandardCsmWidth && (
                        <p style={{ color: "red" }}>
                          WARNING Width is outside of standards.
                          {nearestValidWidth !== null &&
                            ` Nearest valid width: ${nearestValidWidth} in.`}
                        </p>
                      )}
                    {showNonStandardSizeWarning && (
                      <p style={{ color: "red" }}>
                        WARNING Width is not valid with the selected length.
                        {nearestValidWidth !== null &&
                          ` Nearest valid width: ${nearestValidWidth} in.`}
                      </p>
                    )}
                  </Col>
                </Form.Group>
              </Col>
            </Row>
            <Row className="mb-3 mechanical-form-full-width">
              <Col>
                <Form.Group controlId="stationEnclosureComments">
                  <Form.Label>
                    <OverlayTrigger
                      placement="top"
                      overlay={renderTooltip("StationEnclosureComments")}
                      delay={{ show: 300, hide: 200 }}
                    >
                      <span>Comments</span>
                    </OverlayTrigger>
                  </Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    name="stationEnclosureComments"
                    value={formData.stationEnclosureComments || ""}
                    onFocus={handleFocus}
                    onBlur={(e) =>
                      handleBlur(e, "Station Enclosure Comments", [], {
                        ID: formData.ID,
                        url: apiUrl,
                        field: "StationEnclosureComments",
                        tableKey,
                      })
                    }
                    disabled={isDisabled || (!roles.Admin && !roles.Apps)}
                    className={fieldClasses.stationEnclosureComments}
                    onChange={handleChange}
                    placeholder="Enter Comments"
                  />
                </Form.Group>
              </Col>
            </Row>
          </div>
        </div>
      )}
    </div>
  );
};

export default StationEnclosure;
